import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { makeFormData } from '../../helpers/formData'
import vehicleCommentMixin from '../../mixins/vehicleCommentMixin'
import gridView from '@/global/components/grid/GridView.vue'
import vehicleCommentGridBlock from './VehicleCommentGridBlock.vue'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { getBooleanFromValue } from '@/global/utils/utils'

export default {
  name: 'comments',

  data () {
    return {
      showCommentsTable: true,
      showCommentsDetails: false,
      commentsDetailsTitle: this.$t('fleet/vehicle.comment'),
      commentEditState: false,
      commentCreateModeState: false,
      commentValidationErrors: {},
      commentPermissions: {}
    }
  },

  mixins: [
    vehicleCommentMixin
  ],

  computed: {
    commentsConfig () {
      return {
        title: this.$t('fleet/vehicle_comment.comments'),
        key: 'comments',
        visible: getBooleanFromValue(this.permissions?.canReadVehicleComment),
        validationErrors: this.commentValidationErrors,
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        editUpdate: (event) => {
          const { edit } = event || {}
          this.commentEditState = !!edit
        },
        createModeUpdate: (event) => {
          const { createMode } = event || {}
          this.commentCreateModeState = !!createMode
        },
        tabContent: [
          // table view
          {
            component: this.tableGridComponent,
            visible: this.showCommentsTable,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-comment',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            rowDblClick: (event) => this.onShowCommentDetails(event),
            mobileConfig: {
              title: this.$t('fleet/vehicle_comment.comments')
            },
            title_and_back_config: {
              title: this.$t('fleet/vehicle_comment.comments')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewComment(),
              showNewBtn: getBooleanFromValue(this.commentPermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: vehicleCommentGridBlock,
                showDetails: () => {
                  this.showCommentsTable = false
                  this.showCommentsDetails = true
                },
                setDetailsTitle: (val) => {
                  this.commentsDetailsTitle = val
                }
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.commentPermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                created_at: {
                  custom_display_value: (item) => {
                    return item?.created_at ? dateService.formatFromUtc(item.created_at, FORMATS.datetimeShort) : ''
                  }
                },
                images: {
                  wrapperStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start'
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/vehicle_range_limit.input_date'),
                  key: 'created_at',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_comment.text'),
                  key: 'body',
                  always_visible: true,
                  component: truncatedTextDisplay
                },
                {
                  label: this.$t('fleet/vehicle_comment.images'),
                  key: 'images',
                  always_visible: true,
                  component: this.tableImageIconWithCounterComponent,
                  disableDefaultClick: true,
                  onClick: (event) => {
                    this.showFilesPreviewDialog({
                      ...event,
                      fieldKey: 'images',
                      removeApiConfig: {
                        module: 'fleet',
                        route: 'vehicle-comment/remove-image/'
                      }
                    }, 'image', 'comments')
                  }
                },
                {
                  label: this.$t('fleet/vehicle_comment.wrote_by'),
                  key: 'user',
                  always_visible: true
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowCommentDetails(event), { visible: getBooleanFromValue(this.commentPermissions?.canRead) }),
                deleteAction(this.$t, (event) => this.onCommentDelete(event), { visible: getBooleanFromValue(this.commentPermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'body',
                    label: this.$t('fleet/vehicle_comment.text'),
                    visible: true,
                    editable: true
                  }
                ]
              }
            }
          },
          // details view
          {
            component: this.tabActionsComponent,
            visible: this.showCommentsDetails,
            title: this.commentsDetailsTitle,
            showEditButtons: true,
            showEditBtn: getBooleanFromValue(this.commentPermissions?.canUpdate),
            showBackButton: {
              onGoBack: () => this.exitFromCommentDetails()
            },
            onCancelTabSave: () => this.exitFromCommentDetails(true),
            onSaveClick: () => this.onSaveComment()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showCommentsDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            rows: [
              {
                disabled: !getBooleanFromValue(this.commentPermissions?.canUpdate),
                fields: [
                  {
                    key: 'body',
                    type: 'textarea',
                    label: this.$t('fleet/vehicle_comment.text'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'images_upload',
                    label: this.$t('fleet/vehicle_comment.images'),
                    type: 'files_upload',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    accept: '/image*',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'images_upload'
                      }, 'image', 'comments')
                    },
                    visible: this.commentEditState || this.commentCreateModeState,
                    editable: true
                  },
                  {
                    key: 'images',
                    label: this.$t('fleet/vehicle/documents_tab.uploaded_images_label'),
                    type: 'files_preview',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'images',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-comment/remove-image/'
                        }
                      }, 'image', 'comments')
                    },
                    visible: !this.commentEditState && !this.commentCreateModeState,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewComment () {
      const { setCreateMode, clearData } = registeredTabsHandlers?.comments || {}

      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }

      this.commentsDetailsTitle = this.$t('fleet/vehicle.new_vehicle_comment')
      this.showCommentsTable = false
      this.showCommentsDetails = true
    },

    exitFromCommentDetails (canceling = false) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave } = registeredTabsHandlers?.comments || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showCommentsTable = true
        this.showCommentsDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        if (!canceling) {
          this.showCommentsTable = true
          this.showCommentsDetails = false
        }

        cancelSave()
      }
      this.commentValidationErrors = {}
    },

    async onSaveComment () {
      try {
        const { getFormData } = registeredTabsHandlers?.comments || {}
        const { fetchData } = registeredTableHandlers?.comments || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}
        const formData = makeFormData(data,
          ['images_upload'],
          {
            vehicle_id: vehicleId
          }
        )

        let apiUrl = 'vehicle-comment'
        const { id: commentId } = data || {}
        if (!this.commentCreateModeState && commentId) {
          formData.append('_method', 'PATCH')
          apiUrl += `/${commentId}`
        }

        await api('fleet').post(apiUrl, formData)
        this.exitFromCommentDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.commentValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
    }
  }
}
